import { isAxiosError } from "axios";
import { useState } from "react";
import { useSendDocumentationAreaUserLink } from "src/apps/hooks/useSendDocumentationAreaUserLink";
import { useNotifications } from "src/apps/hooks/useNotifications";
import { HTTPError } from "src/contexts/_shared/domain/errors/HTTPError";
import { NotificationStyles } from "src/contexts/_shared/domain/NotificationStyles";
import { createActorAccessNotification } from "src/apps/hooks/createActorAccessNotification";

const TITLE = "documentation_area.access_actions.give_access.modal.title";
const SUCCESS_TITLE = "documentation_area.access_actions.give_access.success.title";
const SUCCESS_MESSAGE = "documentation_area.access_actions.give_access.success.message";
const RESEND_COOLDOWN_CODE = "documentation_area_user_link_resend_cooldown";
const RESEND_COOLDOWN_TITLE = "documentation_area.access_actions.give_access.cooldown.title";
const RESEND_COOLDOWN_MESSAGE = "documentation_area.access_actions.give_access.cooldown.message";
const INVALID_EMAIL = "common.error.invalid_email";
const GENERIC_ERROR = "common.error.generic";

export const useActorUserAccessForm = (onClose: () => void) => {
  const { sendActorUserLink, isPending } = useSendDocumentationAreaUserLink();
  const { addNotifications } = useNotifications();
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  const resetAndClose = () => {
    setEmail("");
    setEmailError("");
    onClose();
  };

  const onEmailChange = (value: string) => {
    setEmail(value);
    setEmailError("");
  };

  const submit = () => {
    if (!isEmailValid(email)) {
      setEmailError(INVALID_EMAIL);
      return;
    }

    sendActorUserLink(email.trim(), {
      onSuccess: () => {
        addNotifications([
          createActorAccessNotification(SUCCESS_TITLE, SUCCESS_MESSAGE, NotificationStyles.SUCCESS),
        ]);
        resetAndClose();
      },
      onError: (error) => {
        const isResendCooldown = isResendCooldownError(error);
        addNotifications([
          createActorAccessNotification(
            isResendCooldown ? RESEND_COOLDOWN_TITLE : TITLE,
            isResendCooldown ? RESEND_COOLDOWN_MESSAGE : GENERIC_ERROR,
            NotificationStyles.ALERT,
          ),
        ]);
        resetAndClose();
      },
    });
  };

  return { email, emailError, isPending, onEmailChange, resetAndClose, submit };
};

const isEmailValid = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const isResendCooldownError = (error: unknown): boolean => {
  if (error instanceof HTTPError) {
    return error.getHTTPCode().isTooManyRequests() && error.getCode() === RESEND_COOLDOWN_CODE;
  }

  return (
    isAxiosError(error) &&
    error.response?.status === 429 &&
    error.response.data?.code === RESEND_COOLDOWN_CODE
  );
};
